import React, { useRef, useEffect, useState, useCallback } from 'react';
import './FruitNinja.css';

const FRUIT_TYPES = [
  { emoji: '🍎', points: 1, radius: 28, color: '#ff4444' },
  { emoji: '🍊', points: 1, radius: 26, color: '#ff8c00' },
  { emoji: '🍋', points: 1, radius: 24, color: '#ffd700' },
  { emoji: '🍉', points: 2, radius: 32, color: '#ff6b6b' },
  { emoji: '🍇', points: 2, radius: 26, color: '#9b59b6' },
  { emoji: '🍓', points: 1, radius: 22, color: '#e74c3c' },
  { emoji: '🥝', points: 2, radius: 24, color: '#2ecc71' },
  { emoji: '🍑', points: 1, radius: 26, color: '#ffb347' },
  { emoji: '🍍', points: 3, radius: 30, color: '#f1c40f' },
];

const BOMB = { emoji: '💣', radius: 28, color: '#333' };

function FruitNinja({ onClose }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({
    fruits: [],
    sliceTrail: [],
    particles: [],
    scorePopups: [],
    score: 0,
    combo: 0,
    maxCombo: 0,
    lives: 3,
    gameOver: false,
    spawnTimer: 0,
    spawnInterval: 150,
    difficulty: 0,
    frameCount: 0,
    mouseDown: false,
    lastMouse: null,
  });
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [gameOver, setGameOver] = useState(false);
  const [combo, setCombo] = useState(0);
  const [bestScore, setBestScore] = useState(() => {
    return parseInt(localStorage.getItem('fruitNinjaBest') || '0', 10);
  });
  const animFrameRef = useRef(null);

  const spawnFruit = useCallback((w, h) => {
    const s = stateRef.current;
    const scale = h / 800;
    const isBomb = Math.random() < 0.08 + s.difficulty * 0.005;
    const type = isBomb ? BOMB : FRUIT_TYPES[Math.floor(Math.random() * FRUIT_TYPES.length)];
    const x = 80 + Math.random() * (w - 160);
    const vx = (Math.random() - 0.5) * 1.5;
    const baseVy = -(5.5 + Math.random() * 2 + s.difficulty * 0.1) * scale;
    s.fruits.push({
      x,
      y: h + 40,
      vx,
      vy: baseVy,
      radius: type.radius,
      emoji: type.emoji,
      points: type.points || 0,
      isBomb: isBomb,
      color: type.color,
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.06,
      sliced: false,
      gravity: (0.03 + s.difficulty * 0.001) * scale,
    });
  }, []);

  const addParticles = useCallback((x, y, color, count = 8) => {
    const s = stateRef.current;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const speed = 2 + Math.random() * 4;
      s.particles.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        decay: 0.02 + Math.random() * 0.02,
        radius: 3 + Math.random() * 4,
        color,
      });
    }
  }, []);

  const addScorePopup = useCallback((x, y, text, color) => {
    stateRef.current.scorePopups.push({ x, y, text, color, life: 1, vy: -2 });
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = canvas.parentElement.clientHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const getPos = (e) => {
      const rect = canvas.getBoundingClientRect();
      if (e.touches) {
        return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
      }
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e) => {
      e.preventDefault();
      const s = stateRef.current;
      s.mouseDown = true;
      s.lastMouse = getPos(e);
      s.sliceTrail = [s.lastMouse];
    };

    const onMove = (e) => {
      e.preventDefault();
      const s = stateRef.current;
      if (!s.mouseDown) return;
      const pos = getPos(e);
      s.sliceTrail.push(pos);
      if (s.sliceTrail.length > 20) s.sliceTrail.shift();

      if (s.lastMouse && !s.gameOver) {
        const dx = pos.x - s.lastMouse.x;
        const dy = pos.y - s.lastMouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist > 5) {
          let slicedAny = false;
          for (const f of s.fruits) {
            if (f.sliced) continue;
            const fx = f.x - pos.x;
            const fy = f.y - pos.y;
            if (Math.sqrt(fx * fx + fy * fy) < f.radius + 10) {
              f.sliced = true;
              if (f.isBomb) {
                s.lives = Math.max(0, s.lives - 1);
                s.combo = 0;
                addParticles(f.x, f.y, '#ff4444', 16);
                addScorePopup(f.x, f.y, '💥', '#ff4444');
                setLives(s.lives);
                setCombo(0);
                if (s.lives <= 0) {
                  s.gameOver = true;
                  if (s.score > bestScore) {
                    localStorage.setItem('fruitNinjaBest', s.score.toString());
                    setBestScore(s.score);
                  }
                  setGameOver(true);
                }
              } else {
                s.combo++;
                const comboMult = s.combo >= 5 ? 3 : s.combo >= 3 ? 2 : 1;
                const earned = f.points * comboMult;
                s.score += earned;
                s.maxCombo = Math.max(s.maxCombo, s.combo);
                slicedAny = true;
                addParticles(f.x, f.y, f.color, 10);
                const label = comboMult > 1 ? `+${earned} x${comboMult}` : `+${earned}`;
                addScorePopup(f.x, f.y, label, comboMult > 1 ? '#ffd700' : '#fff');
                setScore(s.score);
                setCombo(s.combo);
              }
            }
          }
          if (!slicedAny && s.combo > 0) {
            // combo keeps until miss or bomb
          }
        }
      }
      s.lastMouse = pos;
    };

    const onUp = (e) => {
      e.preventDefault();
      const s = stateRef.current;
      s.mouseDown = false;
      s.lastMouse = null;
      if (s.combo > 0) {
        s.combo = 0;
        setCombo(0);
      }
    };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onUp);
    canvas.addEventListener('touchstart', onDown, { passive: false });
    canvas.addEventListener('touchmove', onMove, { passive: false });
    canvas.addEventListener('touchend', onUp, { passive: false });

    const loop = () => {
      const s = stateRef.current;
      const w = canvas.width;
      const h = canvas.height;
      s.frameCount++;

      if (s.frameCount % 900 === 0) {
        s.difficulty = Math.min(s.difficulty + 0.2, 5);
        s.spawnInterval = Math.max(80, 150 - s.difficulty * 8);
      }

      // spawn
      if (!s.gameOver) {
        s.spawnTimer++;
        if (s.spawnTimer >= s.spawnInterval) {
          s.spawnTimer = 0;
          spawnFruit(w, h);
        }
      }

      // update fruits
      for (let i = s.fruits.length - 1; i >= 0; i--) {
        const f = s.fruits[i];
        f.x += f.vx;
        f.y += f.vy;
        f.vy += f.gravity;
        f.rotation += f.rotSpeed;
        if (f.y > h + 80) {
          if (!f.sliced && !f.isBomb && !s.gameOver) {
            s.lives = Math.max(0, s.lives - 1);
            setLives(s.lives);
            if (s.lives <= 0) {
              s.gameOver = true;
              if (s.score > bestScore) {
                localStorage.setItem('fruitNinjaBest', s.score.toString());
                setBestScore(s.score);
              }
              setGameOver(true);
            }
          }
          s.fruits.splice(i, 1);
        }
      }

      // update particles
      for (let i = s.particles.length - 1; i >= 0; i--) {
        const p = s.particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1;
        p.life -= p.decay;
        if (p.life <= 0) s.particles.splice(i, 1);
      }

      // update score popups
      for (let i = s.scorePopups.length - 1; i >= 0; i--) {
        const p = s.scorePopups[i];
        p.y += p.vy;
        p.life -= 0.02;
        if (p.life <= 0) s.scorePopups.splice(i, 1);
      }

      // --- DRAW ---
      ctx.clearRect(0, 0, w, h);

      // slice trail
      if (s.sliceTrail.length > 2 && s.mouseDown) {
        ctx.beginPath();
        ctx.moveTo(s.sliceTrail[0].x, s.sliceTrail[0].y);
        for (let i = 1; i < s.sliceTrail.length; i++) {
          ctx.lineTo(s.sliceTrail[i].x, s.sliceTrail[i].y);
        }
        ctx.strokeStyle = 'rgba(255,255,255,0.7)';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.shadowColor = '#ff8000';
        ctx.shadowBlur = 12;
        ctx.stroke();
        ctx.shadowBlur = 0;

        const innerTrail = s.sliceTrail.slice(-10);
        if (innerTrail.length > 1) {
          ctx.beginPath();
          ctx.moveTo(innerTrail[0].x, innerTrail[0].y);
          for (let i = 1; i < innerTrail.length; i++) {
            ctx.lineTo(innerTrail[i].x, innerTrail[i].y);
          }
          ctx.strokeStyle = 'rgba(255,128,0,0.5)';
          ctx.lineWidth = 6;
          ctx.stroke();
        }
      }

      // fruits
      for (const f of s.fruits) {
        if (f.sliced) continue;
        ctx.save();
        ctx.translate(f.x, f.y);
        ctx.rotate(f.rotation);
        ctx.font = `${f.radius * 1.6}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(f.emoji, 0, 0);
        ctx.restore();
      }

      // particles
      for (const p of s.particles) {
        ctx.globalAlpha = p.life;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * p.life, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // score popups
      for (const p of s.scorePopups) {
        ctx.globalAlpha = p.life;
        ctx.font = 'bold 24px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = p.color;
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 4;
        ctx.fillText(p.text, p.x, p.y);
        ctx.shadowBlur = 0;
      }
      ctx.globalAlpha = 1;

      // fade trail
      if (!s.mouseDown && s.sliceTrail.length > 0) {
        s.sliceTrail = [];
      }

      animFrameRef.current = requestAnimationFrame(loop);
    };

    animFrameRef.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('mouseleave', onUp);
      canvas.removeEventListener('touchstart', onDown);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onUp);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [spawnFruit, addParticles, addScorePopup, bestScore]);

  const restart = () => {
    const s = stateRef.current;
    s.fruits = [];
    s.sliceTrail = [];
    s.particles = [];
    s.scorePopups = [];
    s.score = 0;
    s.combo = 0;
    s.maxCombo = 0;
    s.lives = 3;
    s.gameOver = false;
    s.spawnTimer = 0;
    s.spawnInterval = 150;
    s.difficulty = 0;
    s.frameCount = 0;
    setScore(0);
    setLives(3);
    setGameOver(false);
    setCombo(0);
  };

  return (
    <div className="fruit-ninja-overlay">
      <div className="fruit-ninja-hud">
        <div className="fruit-ninja-hud-left">
          <div className="fruit-ninja-score">{score}</div>
          <div className="fruit-ninja-best">Best: {bestScore}</div>
        </div>
        <div className="fruit-ninja-hud-center">
          {combo >= 3 && (
            <div className={`fruit-ninja-combo ${combo >= 5 ? 'mega' : ''}`}>
              COMBO x{combo}!
            </div>
          )}
        </div>
        <div className="fruit-ninja-hud-right">
          <div className="fruit-ninja-lives">
            {Array.from({ length: 3 }).map((_, i) => (
              <span key={i} className={i < lives ? 'life-active' : 'life-lost'}>
                {i < lives ? '❤️' : '🖤'}
              </span>
            ))}
          </div>
          <button className="fruit-ninja-close" onClick={onClose} title="Закрыть">✕</button>
        </div>
      </div>

      <canvas ref={canvasRef} className="fruit-ninja-canvas" />

      {gameOver && (
        <div className="fruit-ninja-gameover">
          <div className="fruit-ninja-gameover-card">
            <h2>Game Over</h2>
            <div className="fruit-ninja-final-score">
              <span className="final-score-label">Счёт</span>
              <span className="final-score-value">{score}</span>
            </div>
            {stateRef.current.maxCombo >= 3 && (
              <div className="fruit-ninja-max-combo">Max combo: x{stateRef.current.maxCombo}</div>
            )}
            {score >= bestScore && score > 0 && (
              <div className="fruit-ninja-new-record">Новый рекорд!</div>
            )}
            <div className="fruit-ninja-gameover-buttons">
              <button className="fruit-ninja-btn restart" onClick={restart}>Ещё раз</button>
              <button className="fruit-ninja-btn quit" onClick={onClose}>Выйти</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FruitNinja;
